import React from 'react'
import { X, User, Clock, Building, Calendar, Mail, Phone, MapPin, Briefcase, CreditCard, AlertCircle } from 'lucide-react'

const ViewEmployeeShiftModal = ({ employeeShift, onClose, jobsMap = {}, branchesMap = {} }) => {
  // Helper function to safely get property value
  const safeGet = (obj, path, defaultValue = 'N/A') => {
    try {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue
      }, obj)
    } catch {
      return defaultValue
    }
  }

  // Helper function to get job name - handles both populated and non-populated data
  const getJobName = (employee) => {
    if (!employee) return 'N/A'
    if (typeof employee.job === 'object' && employee.job?.name) {
      return employee.job.name
    }
    // Use lookup map to convert ID to name
    if (employee.job && jobsMap[employee.job]) {
      return jobsMap[employee.job]
    }
    return employee.job || 'N/A'
  }

  // Helper function to get branch name - handles both populated and non-populated data
  const getBranchName = (employee) => {
    if (!employee) return 'N/A'
    if (typeof employee.branch === 'object' && employee.branch?.name) {
      return employee.branch.name
    }
    // Use lookup map to convert ID to name
    if (employee.branch && branchesMap[employee.branch]) {
      return branchesMap[employee.branch]
    }
    return employee.branch || 'N/A'
  }

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString()
  }

  // Format date with time
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString()
  }

  // Get status badge color
  const getStatusBadge = (status) => {
    const colors = {
      open: 'bg-green-100 text-green-800',
      close: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
      inactive: 'bg-gray-100 text-gray-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Employee Shift Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Shift Information */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-900 mb-3 flex items-center">
              <Clock className="h-5 w-5 mr-2" />
              Shift Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-blue-700 font-medium">Shift Name</label>
                <p className="text-blue-900">{safeGet(employeeShift, 'shift.shiftName')}</p>
              </div>
              <div>
                <label className="text-sm text-blue-700 font-medium">Shift Code</label>
                <p className="text-blue-900">{safeGet(employeeShift, 'shift.shiftCode')}</p>
              </div>
              <div>
                <label className="text-sm text-blue-700 font-medium">Working Hours</label>
                <p className="text-blue-900">{safeGet(employeeShift, 'shift.workingHours')} hours</p>
              </div>
              <div>
                <label className="text-sm text-blue-700 font-medium">Status</label>
                <div>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(employeeShift.status)}`}>
                    {employeeShift.status || 'N/A'}
                  </span>
                </div>
              </div>
              <div>
                <label className="text-sm text-blue-700 font-medium">Break Hours</label>
                <p className="text-blue-900">{safeGet(employeeShift, 'shift.breakHours')} hours</p>
              </div>
              <div>
                <label className="text-sm text-blue-700 font-medium">Shift Time</label>
                <p className="text-blue-900">
                  {safeGet(employeeShift, 'shift.startTime')} - {safeGet(employeeShift, 'shift.endTime')}
                </p>
              </div>
            </div>
            {safeGet(employeeShift, 'shift.notes') !== 'N/A' && (
              <div className="mt-3">
                <label className="text-sm text-blue-700 font-medium">Notes</label>
                <p className="text-blue-900">{safeGet(employeeShift, 'shift.notes')}</p>
              </div>
            )}
          </div>

          {/* Assignment Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
              <Calendar className="h-5 w-5 mr-2" />
              Assignment Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-600 font-medium">Start Date</label>
                <p className="text-gray-900">{formatDate(employeeShift.startDate)}</p>
              </div>
              <div>
                <label className="text-sm text-gray-600 font-medium">End Date</label>
                <p className="text-gray-900">{formatDate(employeeShift.endDate)}</p>
              </div>
              <div>
                <label className="text-sm text-gray-600 font-medium">Is Active</label>
                <p className="text-gray-900">
                  {employeeShift.isActive ? (
                    <span className="text-green-600">Yes</span>
                  ) : (
                    <span className="text-red-600">No</span>
                  )}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-600 font-medium">Created Date</label>
                <p className="text-gray-900">{formatDate(employeeShift.createdAt)}</p>
              </div>
            </div>
          </div>

          {/* Employee Information */}
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-900 mb-3 flex items-center">
              <User className="h-5 w-5 mr-2" />
              Employee Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-green-700 font-medium">Full Name</label>
                <p className="text-green-900">{safeGet(employeeShift, 'employee.fullName')}</p>
              </div>
              <div>
                <label className="text-sm text-green-700 font-medium">Employee ID</label>
                <p className="text-green-900">
                  {safeGet(employeeShift, 'employee.employeeId', '') || 
                   safeGet(employeeShift, 'employee._id', '')?.slice(-8).toUpperCase() || 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm text-green-700 font-medium">Gender</label>
                <p className="text-green-900 capitalize">{safeGet(employeeShift, 'employee.gender')}</p>
              </div>
              <div>
                <label className="text-sm text-green-700 font-medium">Employee Type</label>
                <p className="text-green-900 capitalize">{safeGet(employeeShift, 'employee.employeeType')}</p>
              </div>
              <div className="flex items-center">
                <Mail className="h-4 w-4 text-green-600 mr-2" />
                <div>
                  <label className="text-sm text-green-700 font-medium">Email</label>
                  <p className="text-green-900">{safeGet(employeeShift, 'employee.emailAddress')}</p>
                </div>
              </div>
              <div className="flex items-center">
                <Phone className="h-4 w-4 text-green-600 mr-2" />
                <div>
                  <label className="text-sm text-green-700 font-medium">Contact</label>
                  <p className="text-green-900">{safeGet(employeeShift, 'employee.contactNumber')}</p>
                </div>
              </div>
              <div className="flex items-center">
                <Briefcase className="h-4 w-4 text-green-600 mr-2" />
                <div>
                  <label className="text-sm text-green-700 font-medium">Job</label>
                  <p className="text-green-900">{getJobName(employeeShift.employee)}</p>
                </div>
              </div>
              <div className="flex items-center">
                <Building className="h-4 w-4 text-green-600 mr-2" />
                <div>
                  <label className="text-sm text-green-700 font-medium">Branch</label>
                  <p className="text-green-900">{getBranchName(employeeShift.employee)}</p>
                </div>
              </div>
            </div>
            
            <div className="mt-3">
              <div className="flex items-start">
                <MapPin className="h-4 w-4 text-green-600 mr-2 mt-1" />
                <div>
                  <label className="text-sm text-green-700 font-medium">Address</label>
                  <p className="text-green-900">{safeGet(employeeShift, 'employee.address')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Emergency Contact Information */}
          {/* {safeGet(employeeShift, 'employee.emergencyContact.name') !== 'N/A' && (
            <div className="bg-orange-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-orange-900 mb-3 flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                Emergency Contact
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-orange-700 font-medium">Name</label>
                  <p className="text-orange-900">{safeGet(employeeShift, 'employee.emergencyContact.name')}</p>
                </div>
                <div>
                  <label className="text-sm text-orange-700 font-medium">Relationship</label>
                  <p className="text-orange-900">{safeGet(employeeShift, 'employee.emergencyContact.relationship')}</p>
                </div>
                <div className="flex items-center">
                  <Phone className="h-4 w-4 text-orange-600 mr-2" />
                  <div>
                    <label className="text-sm text-orange-700 font-medium">Contact Number</label>
                    <p className="text-orange-900">{safeGet(employeeShift, 'employee.emergencyContact.contactNumber')}</p>
                  </div>
                </div>
              </div>
            </div>
          )} */}

          {/* Bank Account Information */}
          {/* {safeGet(employeeShift, 'employee.bankAccount.accountName') !== 'N/A' && (
            <div className="bg-purple-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-purple-900 mb-3 flex items-center">
                <CreditCard className="h-5 w-5 mr-2" />
                Bank Account
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-purple-700 font-medium">Account Name</label>
                  <p className="text-purple-900">{safeGet(employeeShift, 'employee.bankAccount.accountName')}</p>
                </div>
                <div>
                  <label className="text-sm text-purple-700 font-medium">Account Number</label>
                  <p className="text-purple-900">{safeGet(employeeShift, 'employee.bankAccount.accountNumber')}</p>
                </div>
                <div>
                  <label className="text-sm text-purple-700 font-medium">Bank Name</label>
                  <p className="text-purple-900">{safeGet(employeeShift, 'employee.bankAccount.bankName')}</p>
                </div>
                <div>
                  <label className="text-sm text-purple-700 font-medium">Bank Branch</label>
                  <p className="text-purple-900">{safeGet(employeeShift, 'employee.bankAccount.bankBranch')}</p>
                </div>
              </div>
            </div>
          )} */}

          {/* System Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-3">System Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-600 font-medium">Created By</label>
                <p className="text-gray-900">{safeGet(employeeShift, 'createdBy.username')}</p>
              </div>
              <div>
                <label className="text-sm text-gray-600 font-medium">Created At</label>
                <p className="text-gray-900">{formatDateTime(employeeShift.createdAt)}</p>
              </div>
              <div>
                <label className="text-sm text-gray-600 font-medium">Updated At</label>
                <p className="text-gray-900">{formatDateTime(employeeShift.updatedAt)}</p>
              </div>
              {safeGet(employeeShift, 'updatedBy') !== 'N/A' && (
                <div>
                  <label className="text-sm text-gray-600 font-medium">Updated By</label>
                  <p className="text-gray-900">{safeGet(employeeShift, 'updatedBy')}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default ViewEmployeeShiftModal
